import { useState, useEffect, useCallback, useRef } from 'react';
import { getJobAssignmentsOverview, getJobAssignmentsFilterOptions } from '../api/recruiter';

interface JobAssignment {
    assignment_id: string;
    job_id: string;
    job_title: string;
    job_status: string;
    job_department: string;
    job_company_name: string;
    recruiter_id: string;
    recruiter_name: string;
    recruiter_email: string;
    recruiter_company_name: string;
    recruiter_company_image: string;
    assignment_date: string;
    is_active: boolean;
    fee_type: string;
    fee_description: string;
    current_target_type: string;
    current_target_value: number;
    assignment_count: number;
    can_answer_extension_fields: boolean;
}

interface PaginationInfo {
    count: number;
    next: number | null;
    previous: number | null;
    page_size: number;
    current_page: number;
    total_pages: number;
}

interface FilterOptions {
    recruiter_companies: string[];
    job_titles: string[];
    client_companies: string[];
}

interface Filters {
    search: string;
    status: string | undefined;
    recruiter_company: string | undefined;
    client_company: string | undefined;
    job_title: string | undefined;
    sort: string;
    aggregateByCompany: boolean;
}

export const useJobAssignments = () => {
    const [loading, setLoading] = useState(true);
    const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
    const [processedAssignments, setProcessedAssignments] = useState<JobAssignment[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({
        recruiter_companies: [],
        job_titles: [],
        client_companies: []
    });
    const [filters, setFilters] = useState<Filters>({
        search: '',
        status: undefined,
        recruiter_company: undefined,
        client_company: undefined,
        job_title: undefined,
        sort: '-assignment_date',
        aggregateByCompany: false,
    });

    const fetchAssignments = useCallback(async (filtersToUse: Filters, page = 1) => {
        try {
            setLoading(true);

            // Clean filters - remove undefined values for API call
            const cleanedFilters: any = {};
            Object.entries(filtersToUse).forEach(([key, value]) => {
                if (value !== undefined && value !== '' && key !== 'aggregateByCompany') {
                    cleanedFilters[key] = value;
                }
            });

            if (filtersToUse.aggregateByCompany) {
                // When aggregating, fetch ALL data and handle pagination client-side
                const allAssignments: JobAssignment[] = [];
                let fetchPage = 1;
                let hasMoreData = true;

                // Fetch all pages
                while (hasMoreData) {
                    const response = await getJobAssignmentsOverview({
                        ...cleanedFilters,
                        page: fetchPage,
                    });

                    allAssignments.push(...response.data.results);
                    hasMoreData = response.data.pagination.next !== null;
                    fetchPage++;
                }

                // Aggregate all data
                const aggregatedAssignments = aggregateByCompany(allAssignments, filtersToUse.sort);

                // Implement client-side pagination
                const pageSize = 10;
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedAggregated = aggregatedAssignments.slice(startIndex, endIndex);

                setProcessedAssignments(paginatedAggregated);

                // Create custom pagination for aggregated data
                const totalPages = Math.ceil(aggregatedAssignments.length / pageSize);
                setPagination({
                    count: aggregatedAssignments.length,
                    next: page < totalPages ? page + 1 : null,
                    previous: page > 1 ? page - 1 : null,
                    page_size: pageSize,
                    current_page: page,
                    total_pages: totalPages
                });
            } else {
                // Normal pagination for non-aggregated data
                const response = await getJobAssignmentsOverview({
                    ...cleanedFilters,
                    page,
                });

                const rawAssignments = response.data.results;
                setProcessedAssignments(rawAssignments);
                setPagination(response.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching job assignments:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchFilterOptions = useCallback(async () => {
        try {
            setFilterOptionsLoading(true);
            const response = await getJobAssignmentsFilterOptions();
            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        } finally {
            setFilterOptionsLoading(false);
        }
    }, []);

    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedFetch = useCallback((filtersToUse: Filters) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            fetchAssignments(filtersToUse, 1); // Always reset to page 1 when filters change
        }, 300);
    }, [fetchAssignments]);

    useEffect(() => {
        fetchFilterOptions();
        fetchAssignments(filters);
        // Only run on component mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cleanup debounce timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const handleFormChange = (_: any, allFieldsData: any) => {
        // Convert Ant Design field objects array to simple key-value object
        const formValues: any = {};
        allFieldsData.forEach((field: any) => {
            if (field.name && field.name.length > 0) {
                const fieldName = field.name[0]; // Get the field name (first item in name array)
                formValues[fieldName] = field.value;
            }
        });

        const newFilters = {
            search: formValues.search?.trim() || '',
            status: formValues.status || undefined,
            recruiter_company: formValues.recruiter_company || undefined,
            client_company: formValues.client_company || undefined,
            job_title: formValues.job_title || undefined,
            sort: filters.sort,
            aggregateByCompany: formValues.aggregateByCompany || false,
        };

        setFilters(newFilters);

        // Always reset to page 1 when filters change, especially when toggling aggregation
        debouncedFetch(newFilters);
    };

    const handleSort = (sortField: string) => {
        const newSort = filters.sort === sortField ? `-${sortField}` : sortField;
        const newFilters = {
            ...filters,
            sort: newSort,
        };

        setFilters(newFilters);

        if (newFilters.aggregateByCompany) {
            // For aggregated data, sort client-side since we have all data
            // Re-aggregate and re-paginate with new sort
            fetchAssignments(newFilters, 1); // Reset to page 1 when sorting
        } else {
            // For normal data, fetch from server with new sort
            fetchAssignments(newFilters, 1);
        }
    };

    const handlePageChange = (page: number) => {
        fetchAssignments(filters, page);
    };

    const refetch = useCallback(() => {
        if (filters.aggregateByCompany) {
            // For aggregated data, always refetch from page 1 since aggregation may have changed
            fetchAssignments(filters, 1);
        } else {
            // For normal data, maintain current page
            fetchAssignments(filters, pagination?.current_page || 1);
        }
    }, [fetchAssignments, filters, pagination?.current_page]);

    // Function to aggregate assignments by company
    const aggregateByCompany = (assignments: JobAssignment[], sortField?: string): JobAssignment[] => {
        const companyGroups: { [key: string]: JobAssignment[] } = {};

        // Group assignments by company name
        assignments.forEach(assignment => {
            const companyKey = assignment.recruiter_company_name;
            if (!companyGroups[companyKey]) {
                companyGroups[companyKey] = [];
            }
            companyGroups[companyKey].push(assignment);
        });

        // Merge data for each company group
        const aggregated = Object.values(companyGroups).map(groupAssignments => {
            // Sort by assignment date (oldest first) to prioritize older data
            const sortedAssignments = groupAssignments.sort((a, b) =>
                new Date(a.assignment_date).getTime() - new Date(b.assignment_date).getTime()
            );

            const baseAssignment = sortedAssignments[0]; // Use oldest as base
            const activeAssignments = groupAssignments.filter(a => a.is_active);
            const canEnrichAny = groupAssignments.some(a => a.can_answer_extension_fields);

            // Create aggregated assignment
            const aggregatedAssignment: JobAssignment = {
                ...baseAssignment,
                assignment_id: `aggregated-${baseAssignment.recruiter_company_name.replace(/\s+/g, '-').toLowerCase()}`,
                recruiter_name: groupAssignments.length > 1
                    ? `${groupAssignments.length} users`
                    : baseAssignment.recruiter_name,
                recruiter_email: groupAssignments.length > 1
                    ? 'Multiple contacts'
                    : baseAssignment.recruiter_email,
                is_active: activeAssignments.length > 0, // Active if any assignment is active
                can_answer_extension_fields: canEnrichAny, // Can enrich if any user can enrich
                assignment_count: groupAssignments.length,
                // Use oldest assignment date
                assignment_date: baseAssignment.assignment_date,
                // For other fields, prefer filled-in values or use oldest
                current_target_type: groupAssignments.find(a => a.current_target_type)?.current_target_type || baseAssignment.current_target_type,
                current_target_value: groupAssignments.find(a => a.current_target_value)?.current_target_value || baseAssignment.current_target_value,
                fee_type: groupAssignments.find(a => a.fee_type)?.fee_type || baseAssignment.fee_type,
            };

            return aggregatedAssignment;
        });

        // Apply sorting if specified
        if (sortField) {
            return aggregated.sort((a, b) => {
                const isDesc = sortField.startsWith('-');
                const field = isDesc ? sortField.substring(1) : sortField;

                let aValue: any = a[field as keyof JobAssignment];
                let bValue: any = b[field as keyof JobAssignment];

                // Handle special cases for sorting
                if (field === 'recruiter_name') {
                    aValue = a.recruiter_company_name; // Sort by company name when aggregated
                    bValue = b.recruiter_company_name;
                } else if (field === 'assignment_date') {
                    aValue = new Date(a.assignment_date).getTime();
                    bValue = new Date(b.assignment_date).getTime();
                } else if (field === 'job_title') {
                    aValue = a.job_title.toLowerCase();
                    bValue = b.job_title.toLowerCase();
                }

                if (aValue < bValue) return isDesc ? 1 : -1;
                if (aValue > bValue) return isDesc ? -1 : 1;
                return 0;
            });
        }

        return aggregated;
    };

    return {
        loading,
        filterOptionsLoading,
        assignments: processedAssignments,
        pagination,
        filterOptions,
        filters,
        handleFormChange,
        handleSort,
        handlePageChange,
        refetch,
    };
};

export type { JobAssignment, PaginationInfo, FilterOptions, Filters }; 